import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Anomalies from './pages/Anomalies';
import Recommendations from './pages/Recommendations';
import Guardrails from './pages/Guardrails';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import TeamCosts from './pages/TeamCosts';
import SavingsTracker from './pages/SavingsTracker';
import WhatIfCalculator from './pages/WhatIfCalculator';
import ResourceScheduler from './pages/ResourceScheduler';
import TaggingCompliance from './pages/TaggingCompliance';
import CostComparison from './pages/CostComparison';
import AIAdvisor from './pages/AIAdvisor';
import CloudIntegrations from './pages/CloudIntegrations';
import CostDNA from './pages/CostDNA';
// v35.19 — GitToBill consolidated into /bill-detective (redirected)
// import GitToBill from './pages/GitToBill';
import CostTimeMachine from './pages/CostTimeMachine';
import AutonomousPilot from './pages/AutonomousPilot';
import CostWeather from './pages/CostWeather';
import CloudPL from './pages/CloudPL';
import TechSpend from './pages/TechSpend';
// v35.19 — CustomerEconomics consolidated into /customer-pl (redirected)
// import CustomerEconomics from './pages/CustomerEconomics';
import CustomerPL from './pages/CustomerPL';
import AdminSecurityProof from './pages/AdminSecurityProof';
import AdminSalesSignals from './pages/AdminSalesSignals';
import PRCostPredictor from './pages/PRCostPredictor';
import CartieLens from './pages/CartieLens';
import CostBisect from './pages/CostBisect';
import BatchRouter from './pages/BatchRouter';
import QAFailover from './pages/QAFailover';
import ReverseTag from './pages/ReverseTag';
import K8sCost from './pages/K8sCost';
import CostCausality from './pages/CostCausality';
import SafeAutonomy from './pages/SafeAutonomy';
import CarbonOptimizer from './pages/CarbonOptimizer';
import ForwardPredict from './pages/ForwardPredict';
import UnitEcon from './pages/UnitEcon';
import Copilot from './pages/Copilot';
import SharedCost from './pages/SharedCost';
import TagCanon from './pages/TagCanon';
import GpuSpot from './pages/GpuSpot';
import Scope3 from './pages/Scope3';
import CausalBot from './pages/CausalBot';
import MultiCurrency from './pages/MultiCurrency';
import FinOpsIntegrations from './pages/FinOpsIntegrations';
import PublicWithNavbar from './components/PublicWithNavbar';
import BackToHomePill from './components/BackToHomePill';
import OverflowAlarm from './components/OverflowAlarm';
import PatentPortfolio from './pages/PatentPortfolio';
import IntegrationsHub from './pages/IntegrationsHub';
import AICostCalculator from './pages/AICostCalculator';
// v34.4 (Mar 2026) — Token Cost Crisis suite + developer marketing
import DevelopersQuickstart from './pages/DevelopersQuickstart';
import SemanticCachePage from './pages/SemanticCachePage';
import PromptCompactorPage from './pages/PromptCompactorPage';
import FinOpsRealityCheck from './pages/FinOpsRealityCheck';
import PilotPage from './pages/PilotPage';
import BudgetNegotiator from './pages/BudgetNegotiator';
import SOC2 from './pages/SOC2';
import Subprocessors from './pages/Subprocessors';
import Connect from './pages/Connect';
import Guarantee from './pages/Guarantee';
import Roadmap from './pages/Roadmap';
import AuditLog from './pages/AuditLog';
// v35.19 — CodeTracer consolidated into /bill-detective (redirected)
// import CodeTracer from './pages/CodeTracer';
import AutomationPolicies from './pages/AutomationPolicies';
import HowItWorks from './pages/HowItWorks';
import HowItSaves from './pages/HowItSaves';
import IdleResourceKiller from './pages/IdleResourceKiller';
import IdleKillerConnect from './pages/IdleKillerConnect';
import BillDetective from './pages/BillDetective';  // Engine #29 — Bill Detective
import ROIContract from './pages/ROIContract';  // Phase 2 Win #2 — signable ROI contract
import WallOfFame from './pages/WallOfFame';  // v35.23 — public viral surface
import CustomerStory from './pages/CustomerStory';  // v35.24 — per-customer story page
import TryDemo from './pages/TryDemo';  // v35.31 — public 60-sec token-savings demo
// Note: /pages/Status.js exists for reference but is unused — /status uses StatusPage
import SavingsCalculator from './pages/SavingsCalculator';
import VSCodeExtension from './pages/VSCodeExtension';
import BadgePage from './pages/BadgePage';
import BillScore from './pages/BillScore';
import Benchmarks from './pages/Benchmarks';
import AIFeatureProfitability from './pages/AIFeatureProfitability';
import PublicAIProfitability from './pages/PublicAIProfitability';
import CommitmentCoach from './pages/CommitmentCoach';
import CommitmentRebalancer from './pages/CommitmentRebalancer';
import ROIPledge from './pages/ROIPledge';
import PilotActivation from './pages/admin/PilotActivation';
import P0Guide from './pages/admin/P0Guide';
import CostActuals from './pages/admin/CostActuals';
import SlackSetup from './pages/admin/SlackSetup';
import AdminSignups from './pages/admin/Leads'; // v34.99 (Feb 14 2026) — phone-friendly waitlist signups dashboard at /admin/signups
import CloudBillingSync from './pages/admin/CloudBillingSync';
import IncidentLog from './pages/admin/IncidentLog';
// v35.18 — ZombieHunter import removed; route now redirects to /idle-killer (Engine #28)
import PaybackFilter from './pages/admin/PaybackFilter';
import ComplianceControls from './pages/admin/ComplianceControls';
import EgressPredictor from './pages/EgressPredictor';
import VirtualTenant from './pages/VirtualTenant';
import SdkPage from './pages/Sdk';
import AnomalyAutopsy from './pages/admin/AnomalyAutopsy';
import PRCostGuardrail from './pages/admin/PRCostGuardrail';
import RoadmapForecast from './pages/admin/RoadmapForecast';
import SaaSSpendIntelligence from './pages/admin/SaaSSpendIntelligence';
import AgentEconomics from './pages/admin/AgentEconomics';
import EmbeddingAudit from './pages/admin/EmbeddingAudit';
import IdleEndpoints from './pages/admin/IdleEndpoints';
import ConversionROI from './pages/admin/ConversionROI';
import PipelineDedup from './pages/admin/PipelineDedup';
import AlignKickoff from './pages/admin/AlignKickoff';
import HotPilots from './pages/admin/HotPilots';
import HNLaunchAdmin from './pages/admin/HNLaunchAdmin';
import CostCenters from './pages/admin/CostCenters';
import HonestScorecard from './pages/HonestScorecard';
import AlgorithmCorrectness from './pages/AlgorithmCorrectness';
import LandingMockupV34 from './pages/LandingMockupV34';
import ReasoningEfficiency from './pages/ReasoningEfficiency';
import OutputDiet from './pages/OutputDiet';
import MemoryCompactor from './pages/MemoryCompactor';
import DecisionLedger from './pages/DecisionLedger';
import ForecastsGraded from './pages/ForecastsGraded';
import CostSlo from './pages/CostSlo';
import PtuPlanner from './pages/PtuPlanner';
import ShadowAISeats from './pages/ShadowAISeats';
import GpuExchange from './pages/GpuExchange';
import MondayBrief from './pages/MondayBrief';
import PolicyPlayground from './pages/PolicyPlayground';
import Changelog from './pages/Changelog';
import Docs from './pages/Docs';
import Status from './pages/Status'; // eslint-disable-line no-unused-vars
import CaseStudyAureliaAI from './pages/CaseStudyAureliaAI';
import SOC2Readiness from './pages/SOC2Readiness';
import CodeOfConduct from './pages/legal/CodeOfConduct';
import RiskAssessment from './pages/legal/RiskAssessment';
import BackupRunbook from './pages/legal/BackupRunbook';
import IncidentResponse from './pages/legal/IncidentResponse';
import CapacityPlanning from './pages/legal/CapacityPlanning';
import SLA from './pages/legal/SLA';
import MsaDpaTemplates from './pages/legal/MsaDpaTemplates';
import SetupIn15Minutes from './pages/SetupIn15Minutes';
import EngineerSavingsWall from './pages/EngineerSavingsWall';
import TagDrift from './pages/TagDrift';
import CostImport from './pages/CostImport';
import PricingPage from './pages/PricingPage';
import DecisionSimulator from './pages/DecisionSimulator';
import PublicTrust from './pages/PublicTrust';
import FreeAudit from './pages/FreeAudit';
import CarbonCost from './pages/CarbonCost';
// Pricing import removed — now uses PricingPage as single source of truth (Iter47)
import KubernetesCosts from './pages/KubernetesCosts';
import Compliance from './pages/Compliance';
import PrivacySettings from './pages/PrivacySettings';
import Security from './pages/Security';
import Landing from './pages/Landing';
import CostBot from './pages/CostBot';
import CostExplainer from './pages/CostExplainer';
import TerraformCostPreview from './pages/TerraformCostPreview';
import VoiceAssistant from './pages/VoiceAssistant';
// v35.19 — PRCostImpact consolidated into /bill-detective (redirected)
// import PRCostImpact from './pages/PRCostImpact';
import CostHealthScore from './pages/CostHealthScore';
import IndustryIntelligence from './pages/IndustryIntelligence';
import CostCollaboration from './pages/CostCollaboration';
import SecurityCenter from './pages/SecurityCenter';
import ExportCenter from './pages/ExportCenter';
import DashboardBuilder from './pages/DashboardBuilder';
import AdvancedSearch from './pages/AdvancedSearch';
import CostIntelligenceGraph from './pages/CostIntelligenceGraph';
import NotificationCenter from './pages/NotificationCenter';
import About from './pages/About';
import Brand from './pages/Brand';
import ShareYourVoice from './pages/ShareYourVoice';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import IntegrationsMarketplace from './pages/IntegrationsMarketplace';
import StatusPage from './pages/StatusPage';
import OrganizationPage from './pages/OrganizationPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import SharedViewerPage from './pages/SharedViewerPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import GuidesIndex from './pages/GuidesIndex';
import GuideDetail from './pages/GuideDetail';
import AdminSEO from './pages/AdminSEO';
import NotFound from './pages/NotFound';
import Refund from './pages/Refund';
import Waitlist from './pages/Waitlist';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import SnowflakeDashboard from './pages/SnowflakeDashboard';
import SnowflakeQueryExplorer from './pages/SnowflakeQueryExplorer';
import SnowflakeAlerts from './pages/SnowflakeAlerts';
import DatabricksDashboard from './pages/DatabricksDashboard';
import DigitalOceanDashboard from './pages/DigitalOceanDashboard';
import SpikeAutopsy from './pages/SpikeAutopsy';
import QueryEstimator from './pages/QueryEstimator';
import BudgetCountdown from './pages/BudgetCountdown';
import TeamChargeback from './pages/TeamChargeback';
import StorageDetective from './pages/StorageDetective';
import BillShockPrevention from './pages/BillShockPrevention';
import SpendFreeze from './pages/SpendFreeze';
// v35.18.2 — OneClickFix merged into Recommendations; route redirects below
// import OneClickFix from './pages/OneClickFix';
import CostConscience from './pages/CostConscience';
import AICFO from './pages/AICFO';
import AnomalyAutopsyPage from './pages/AnomalyAutopsy'; // v34.78 — fixes /anomaly-autopsy 404, linked from hero chip
import PeerBenchmark from './pages/PeerBenchmark';
import AICommandCenter from './pages/AICommandCenter';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import CostForecasting from './pages/CostForecasting';
import WeeklyDigest from './pages/WeeklyDigest';
import ABTesting from './pages/ABTesting';
import AdminSettings from './pages/AdminSettings';
import AdminServerModules from './pages/AdminServerModules';
import AdminPreFlightPage from './pages/AdminPreFlightPage';
import AdminRouterMap from './pages/AdminRouterMap';
import AdminAuditLog from './pages/AdminAuditLog';
import SOC2Evidence from './pages/admin/SOC2Evidence';
import AdminFeatureCoverage from './pages/AdminFeatureCoverage';
import OrgSlackSettings from './pages/OrgSlackSettings';
import AdminLeads from './pages/AdminLeads';
import AdminConversionFunnel from './pages/AdminConversionFunnel';
import BuildPage from './pages/BuildPage';
import BuildMomentumPage from './pages/BuildMomentumPage';
import BuildApiPage from './pages/BuildApiPage';
// BuildBatchPage route removed (Iter84d) — was leaking per-module endpoint lists.
// LaunchPage deleted (Iter84k) — homepage / already does the launch job;
// no need for a "LAUNCHING TODAY" badge that ages poorly.
import PaymentSuccess from './pages/PaymentSuccess';
import AIGPUCostIntelligence from './pages/AIGPUCostIntelligence';
// v35.18 — ZombieResourceKiller import removed; route now redirects to /idle-killer (Engine #28)
import CommitmentIntelligence from './pages/CommitmentIntelligence';
import InvoiceAutopsy from './pages/InvoiceAutopsy';
// v35.19 — CostScorecard consolidated into /bill-detective (redirected)
// import CostScorecard from './pages/CostScorecard';
import SaaSDetective from './pages/SaaSDetective';
import SmartTagFixer from './pages/SmartTagFixer';
import EgressShield from './pages/EgressShield';
import BoardDeckGenerator from './pages/BoardDeckGenerator';
import VendorDealIntel from './pages/VendorDealIntel';
import ComplianceMapper from './pages/ComplianceMapper';
import CloudAudit from './pages/CloudAudit';
import UnitEconomics from './pages/UnitEconomics';
import EnterpriseSSOPage from './pages/EnterpriseSSOPage';
import SSOLogin from './pages/SSOLogin';
import SnowflakeHealthScore from './pages/SnowflakeHealthScore';
import SnowflakeAuditTool from './pages/SnowflakeAuditTool';
import Developers from './pages/Developers';
import ApiKeys from './pages/ApiKeys';
import FAQ from './pages/FAQ';
import TwitterDrafts from './pages/TwitterDrafts';
import ReferralLanding from './pages/ReferralLanding';
import Referrals from './pages/Referrals';
import PromptCostPredictor from './pages/PromptCostPredictor';
import TokenIntelligence from './pages/TokenIntelligence';
import TokenPricingIndex from './pages/TokenPricingIndex'; // v34.94 — public LLM pricing index at /tokens
import AwsBillAudit from './pages/AwsBillAudit'; // v34.96 — focused AWS CSV audit page
import BillAudit from './pages/BillAudit'; // v34.97 — generic cloud audit (AWS, Azure, GCP, Snowflake, Databricks, DigitalOcean)
import CloudBillAuditHub from './pages/CloudBillAuditHub'; // v34.97 — hub for all 6 cloud audits
import ChatBot from './pages/ChatBot'; // v34.98 — Slack + Teams bot waitlist (unified)
import FineTuningROI from './pages/FineTuningROI';
import TokenIntelligenceSuite from './pages/TokenIntelligenceSuite';
import OgTokenIntelligenceSuite from './pages/OgTokenIntelligenceSuite';
import Engine27 from './pages/Engine27';
import AdminEngine27 from './pages/admin/AdminEngine27';
import OgHome from './pages/OgHome';
import BillingSettings from './pages/BillingSettings';
import VsIndex from './pages/VsIndex';
import VsVendor from './pages/VsVendor';
import CostAllocation from './pages/CostAllocation';
import FreeTools from './pages/FreeTools';
import BetaRedeem from './pages/BetaRedeem';
import AdminBeta from './pages/AdminBeta';
import PromptPlayground from './pages/PromptPlayground';
import PromptLibrary from './pages/PromptLibrary';
import TraceViewer from './pages/TraceViewer';
import EvalRunner from './pages/EvalRunner';
import LlmProxy from './pages/LlmProxy';
import CliPage from './pages/CliPage';  // v34.33 — public /cli landing for @cartieai/cli + MCP server
import SlackBotPage from './pages/SlackBotPage';  // v34.37 — public /slack-bot install + docs page
import WallOfTenantsPage from './pages/WallOfTenantsPage';  // v34.42 — public /wall-of-tenants showcase page
import SaasSpend from './pages/SaasSpend';
import FeedbackWidget from './components/FeedbackWidget';
import InstallAppPrompt from './components/InstallAppPrompt';
import CookieConsent from './components/CookieConsent';
import './App.css';

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <BackToHomePill />
          <OverflowAlarm />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/sign-up" element={<Login />} />
            <Route path="/join" element={<Login />} />
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/security" element={<Security />} />
            <Route path="/trust" element={<PublicTrust />} />
            <Route path="/free-audit" element={<FreeAudit />} />  {/* v35.70 — outbound lead-capture */}
            <Route path="/subprocessors" element={<Subprocessors />} />
            <Route path="/connect" element={<Connect />} />
            <Route path="/guarantee" element={<Guarantee />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/audit-log" element={<AuditLog />} />
            {/* v35.19 — /code-tracer consolidated into /bill-detective (Engine #29 Code→Cost tab). */}
            <Route path="/code-tracer" element={<Navigate to="/bill-detective" replace />} />

            {/* v35.28 — Honest dedupe (Feb 2026). 9 duplicate engines collapsed into their
                canonical homes. Old deep-links keep working via these redirects. */}
            <Route path="/invoice-autopsy"        element={<Navigate to="/bill-detective" replace />} />
            <Route path="/spike-autopsy"          element={<Navigate to="/bill-detective" replace />} />
            <Route path="/causal-bot"             element={<Navigate to="/bill-detective" replace />} />
            <Route path="/batch-router"           element={<Navigate to="/engine-27" replace />} />
            <Route path="/token-intelligence"     element={<Navigate to="/anomaly-detection" replace />} />
            <Route path="/engineer-savings-wall"  element={<Navigate to="/wall-of-fame" replace />} />
            <Route path="/policies/automation" element={<AutomationPolicies />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/how-it-saves" element={<HowItSaves />} />
            <Route path="/idle-killer" element={<IdleResourceKiller />} />
            <Route path="/idle-killer/connect" element={<IdleKillerConnect />} />
            <Route path="/bill-detective" element={<BillDetective />} />
            <Route path="/wall-of-fame" element={<WallOfFame />} />  {/* v35.23 — public viral surface */}
            <Route path="/wall-of-fame/c/:slug" element={<CustomerStory />} />  {/* v35.24 — per-customer story page */}
            <Route path="/try" element={<TryDemo />} />  {/* v35.31 — public live token-savings demo */}
            <Route path="/pricing/contract" element={<ROIContract />} />  {/* Phase 2 Win #2 */}

            {/* Patent sub-system pages — public routes that auto-show Navbar when user is logged in */}
            <Route element={<PublicWithNavbar />}>
              <Route path="/pr-cost-predictor" element={<PRCostPredictor />} />
              <Route path="/cartie-lens" element={<CartieLens />} />
              <Route path="/cost-bisect" element={<CostBisect />} />
              <Route path="/batch-router" element={<BatchRouter />} />
              <Route path="/qa-failover" element={<QAFailover />} />
              <Route path="/reverse-tag" element={<ReverseTag />} />
              <Route path="/k8s-cost" element={<K8sCost />} />
              <Route path="/cost-causality" element={<CostCausality />} />
              <Route path="/safe-autonomy" element={<SafeAutonomy />} />
              <Route path="/carbon-cost" element={<CarbonOptimizer />} />
              <Route path="/forward-predict" element={<ForwardPredict />} />
              <Route path="/unit-econ" element={<UnitEcon />} />
              <Route path="/copilot" element={<Copilot />} />
              <Route path="/shared-cost" element={<SharedCost />} />
              <Route path="/tag-canon" element={<TagCanon />} />
              <Route path="/gpu-spot" element={<GpuSpot />} />
              <Route path="/scope3" element={<Scope3 />} />
              <Route path="/causal-bot" element={<CausalBot />} />
              <Route path="/multi-currency" element={<MultiCurrency />} />
              <Route path="/patent-portfolio" element={<PatentPortfolio />} />
              <Route path="/egress-predictor" element={<EgressPredictor />} />
              <Route path="/virtual-tenant" element={<VirtualTenant />} />
              <Route path="/sdk" element={<SdkPage />} />
            </Route>
            <Route path="/integrations" element={<IntegrationsHub />} />
            <Route path="/integrations/data-providers" element={<FinOpsIntegrations />} />
            <Route path="/tools/ai-cost-calculator" element={<AICostCalculator />} />
            <Route path="/ai-cost-calculator" element={<AICostCalculator />} />
            <Route path="/anomaly-autopsy" element={<AnomalyAutopsyPage />} />
            {/* v34.4 (Mar 2026) — Token Cost Crisis suite + dev marketing */}
            <Route path="/developers/quickstart" element={<DevelopersQuickstart />} />
            <Route path="/semantic-cache" element={<SemanticCachePage />} />
            <Route path="/prompt-compactor" element={<PromptCompactorPage />} />
            <Route path="/tools/finops-maturity" element={<FinOpsRealityCheck />} />
            <Route path="/finops-maturity" element={<FinOpsRealityCheck />} />
            <Route path="/finops-reality-check" element={<FinOpsRealityCheck />} />
            <Route path="/tools/finops-reality-check" element={<FinOpsRealityCheck />} />
            <Route path="/pilot" element={<PilotPage />} />
            <Route path="/pilot-tier" element={<PilotPage />} />
            <Route path="/budget-negotiator" element={<BudgetNegotiator />} />
            <Route path="/soc2" element={<SOC2 />} />
            <Route path="/savings-calculator" element={<SavingsCalculator />} />
            <Route path="/vscode-extension" element={<VSCodeExtension />} />
            <Route path="/badge" element={<BadgePage />} />
            <Route path="/bill-score" element={<BillScore />} />
            <Route path="/benchmarks" element={<Benchmarks />} />
            <Route path="/ai-profitability" element={<PublicAIProfitability />} />
            <Route path="/commitment-coach" element={<CommitmentCoach />} />
            <Route path="/commitment-rebalancer" element={<CommitmentRebalancer />} />
            <Route path="/roi-pledge" element={<ROIPledge />} />
            <Route path="/honest-scorecard" element={<HonestScorecard />} />
            <Route path="/algorithm-correctness" element={<AlgorithmCorrectness />} />
            <Route path="/landing-v34-preview" element={<LandingMockupV34 />} />
            <Route path="/reasoning-efficiency" element={<ReasoningEfficiency />} />
            <Route path="/output-diet" element={<OutputDiet />} />
            <Route path="/memory-compactor" element={<MemoryCompactor />} />
            <Route path="/decision-ledger" element={<DecisionLedger />} />
            <Route path="/forecasts-graded" element={<ForecastsGraded />} />
            <Route path="/cost-slo" element={<CostSlo />} />
            <Route path="/ptu-planner" element={<PtuPlanner />} />
            <Route path="/shadow-ai-seats" element={<ShadowAISeats />} />
            <Route path="/gpu-exchange" element={<GpuExchange />} />
            <Route path="/monday-brief" element={<MondayBrief />} />
            <Route path="/policy-playground" element={<PolicyPlayground />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/docs" element={<Docs />} />
            {/* v34.93 — /status now serves the LIVE StatusPage (was Status.js, kept on disk for history).
                Old Status.js leaked "MongoDB cluster" — new one uses generic labels from /api/status. */}
            <Route path="/status" element={<StatusPage />} />
            {/* v34.94 — Public LLM token-pricing index. SEO + lead-gen magnet. */}
            <Route path="/tokens" element={<TokenPricingIndex />} />
            <Route path="/token-pricing" element={<TokenPricingIndex />} />
            <Route path="/llm-pricing" element={<TokenPricingIndex />} />
            {/* v34.96 — Focused AWS CSV bill-audit page (matches Landing card promise). */}
            <Route path="/aws-bill-audit" element={<AwsBillAudit />} />
            <Route path="/aws-audit" element={<AwsBillAudit />} />
            {/* v34.97 — Multi-cloud bill audits + hub. Generic BillAudit component
                with a per-cloud config. All routes use the same backend endpoint. */}
            <Route path="/cloud-bill-audit" element={<CloudBillAuditHub />} />
            <Route path="/azure-bill-audit" element={<BillAudit cloud="azure" />} />
            <Route path="/gcp-bill-audit" element={<BillAudit cloud="gcp" />} />
            <Route path="/snowflake-bill-audit" element={<BillAudit cloud="snowflake" />} />
            <Route path="/databricks-bill-audit" element={<BillAudit cloud="databricks" />} />
            <Route path="/digitalocean-bill-audit" element={<BillAudit cloud="digitalocean" />} />
            {/* v34.98 — Unified Slack + Teams bot waitlist page. Honest pre-launch
                framing: most enterprises use Teams, not Slack — show both. */}
            <Route path="/chat-bot" element={<ChatBot />} />
            <Route path="/teams-bot" element={<ChatBot />} />
            <Route path="/case-study/aurelia-ai" element={<CaseStudyAureliaAI />} />
            <Route path="/soc2-readiness" element={<SOC2Readiness />} />
            <Route path="/legal/code-of-conduct" element={<CodeOfConduct />} />
            <Route path="/legal/risk-assessment" element={<RiskAssessment />} />
            <Route path="/legal/backup-runbook" element={<BackupRunbook />} />
            <Route path="/legal/incident-response" element={<IncidentResponse />} />
            <Route path="/legal/capacity-planning" element={<CapacityPlanning />} />
            <Route path="/legal/sla" element={<SLA />} />
            <Route path="/legal/templates" element={<MsaDpaTemplates />} />
            <Route path="/legal/msa" element={<MsaDpaTemplates />} />
            <Route path="/legal/dpa" element={<MsaDpaTemplates />} />
            <Route path="/setup-in-15-minutes" element={<SetupIn15Minutes />} />
            <Route path="/engineer-savings-wall" element={<EngineerSavingsWall />} />
            <Route path="/tag-drift" element={<TagDrift />} />
            <Route path="/cost-import" element={<CostImport />} />
            <Route path="/decision-simulator" element={<DecisionSimulator />} />
            <Route path="/about" element={<About />} />
            <Route path="/brand" element={<Brand />} />
            <Route path="/share-your-voice" element={<ShareYourVoice />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/system-status" element={<StatusPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuideDetail />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/build" element={<BuildPage />} />
            <Route path="/build/momentum" element={<BuildMomentumPage />} />
            <Route path="/build/api" element={<BuildApiPage />} />
            {/* /build/batch/:module route REMOVED (Iter84d) — was exposing per-module
                 endpoint lists + feature names to anyone who could guess a slug. */}
            {/* /launch route DELETED (Iter84k) — homepage / does the same job */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/sso/login" element={<SSOLogin />} />
            <Route path="/snowflake-health-score" element={<SnowflakeHealthScore />} />
            <Route path="/free-snowflake-audit" element={<SnowflakeAuditTool />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/prompt-cost-predictor" element={<PromptCostPredictor />} />
            <Route path="/fine-tuning-roi" element={<FineTuningROI />} />
            <Route path="/token-intelligence-suite" element={<TokenIntelligenceSuite />} />
            <Route path="/og/token-intelligence-suite" element={<OgTokenIntelligenceSuite />} />
            <Route path="/engine-27" element={<Engine27 />} />
            <Route path="/admin/engine27" element={<AdminEngine27 />} />
            <Route path="/og/home" element={<OgHome />} />
            <Route path="/settings/billing" element={<BillingSettings />} />
            <Route path="/vs" element={<VsIndex />} />
            <Route path="/vs/:vendor" element={<VsVendor />} />
            <Route path="/allocation" element={<CostAllocation />} />
            <Route path="/free-tools" element={<FreeTools />} />
            <Route path="/beta" element={<BetaRedeem />} />
            <Route path="/admin/beta" element={<AdminBeta />} />
            {/* LLM Engineering Suite (Feb 2026) — Playground (public), Library/Traces/Evals (authed) */}
            <Route path="/prompt-playground" element={<PromptPlayground />} />
            <Route path="/prompts" element={<PromptLibrary />} />
            <Route path="/prompts/:id" element={<PromptLibrary />} />
            <Route path="/traces" element={<TraceViewer />} />
            <Route path="/traces/:id" element={<TraceViewer />} />
            <Route path="/evals" element={<EvalRunner />} />
            <Route path="/llm-proxy" element={<LlmProxy />} />
            <Route path="/cli" element={<CliPage />} />
            <Route path="/slack-bot" element={<SlackBotPage />} />
            <Route path="/wall-of-tenants" element={<WallOfTenantsPage />} />
            <Route path="/saas-spend" element={<SaasSpend />} />
            <Route path="/invite/:token" element={<AcceptInvitationPage />} />
            <Route path="/s/:token" element={<SharedViewerPage />} />
            <Route path="/share/:token" element={<SharedViewerPage />} />
            <Route path="/billing" element={<PricingPage />} />
            <Route path="/upgrade" element={<PricingPage />} />
            
            {/* Protected Routes (with Layout) */}
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/anomalies" element={<Anomalies />} />
              <Route path="/recommendations" element={<Recommendations />} />
              {/* Company pages inside app for logged-in users */}
              <Route path="/app/about" element={<About isInternal={true} />} />
              <Route path="/app/careers" element={<Careers isInternal={true} />} />
              <Route path="/app/contact" element={<Contact isInternal={true} />} />
              <Route path="/app/blog" element={<Blog isInternal={true} />} />
              <Route path="/recommendations" element={<Recommendations />} />
              <Route path="/guardrails" element={<Guardrails />} />
              <Route path="/teams" element={<TeamCosts />} />
              <Route path="/savings" element={<SavingsTracker />} />
              <Route path="/calculator" element={<WhatIfCalculator />} />
              <Route path="/scheduler" element={<ResourceScheduler />} />
              <Route path="/tagging" element={<TaggingCompliance />} />
              <Route path="/comparison" element={<CostComparison />} />
              <Route path="/ai-advisor" element={<AIAdvisor />} />
              <Route path="/cloud-integrations" element={<CloudIntegrations />} />
              <Route path="/cost-dna" element={<CostDNA />} />
              {/* v35.19 — /git-to-bill consolidated into /bill-detective (Engine #29 Code→Cost tab). */}
              <Route path="/git-to-bill" element={<Navigate to="/bill-detective" replace />} />
              <Route path="/time-machine" element={<CostTimeMachine />} />
              <Route path="/pilot" element={<AutonomousPilot />} />
              <Route path="/weather" element={<CostWeather />} />
              <Route path="/cloud-pl" element={<CloudPL />} />
              <Route path="/tech-spend" element={<TechSpend />} />
              {/* v35.19 — /customer-economics consolidated into /customer-pl (richer page) + /bill-detective Per-Customer tab. */}
              <Route path="/customer-economics" element={<Navigate to="/customer-pl" replace />} />
              <Route path="/customer-pl" element={<CustomerPL />} />
              <Route path="/ai-feature-profitability" element={<AIFeatureProfitability />} />
              <Route path="/settings/api-keys" element={<ApiKeys />} />
              <Route path="/admin/twitter-drafts" element={<TwitterDrafts />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/token-intelligence" element={<TokenIntelligence />} />
              <Route path="/admin/security-proof" element={<AdminSecurityProof />} />
              <Route path="/admin/sales-signals" element={<AdminSalesSignals />} />
              <Route path="/carbon" element={<CarbonCost />} />
              <Route path="/kubernetes" element={<KubernetesCosts />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/privacy" element={<PrivacySettings />} />
              <Route path="/cost-bot" element={<CostBot />} />
              <Route path="/cost-explainer" element={<CostExplainer />} />
              <Route path="/terraform-preview" element={<TerraformCostPreview />} />
              <Route path="/ai-gpu" element={<AIGPUCostIntelligence />} />
              {/* v35.18 — /zombie-killer consolidated into /idle-killer (Engine #28). Redirect to keep old links alive. */}
              <Route path="/zombie-killer" element={<Navigate to="/idle-killer" replace />} />
              <Route path="/commitment-intel" element={<CommitmentIntelligence />} />
              {/* New Pain Point Features */}
              <Route path="/invoice-autopsy" element={<InvoiceAutopsy />} />
              {/* v35.19 — /cost-scorecard consolidated into /bill-detective (Engine #29 Engineer Leaderboard tab). Backend cron still fires Monday Slack/email digests. */}
              <Route path="/cost-scorecard" element={<Navigate to="/bill-detective" replace />} />
              <Route path="/saas-detective" element={<SaaSDetective />} />
              <Route path="/smart-tags" element={<SmartTagFixer />} />
              <Route path="/egress-shield" element={<EgressShield />} />
              <Route path="/board-deck" element={<BoardDeckGenerator />} />
              <Route path="/vendor-deals" element={<VendorDealIntel />} />
              <Route path="/compliance-mapper" element={<ComplianceMapper />} />
              <Route path="/cloud-audit" element={<CloudAudit />} />
              <Route path="/enterprise-sso" element={<EnterpriseSSOPage />} />
              <Route path="/unit-economics" element={<UnitEconomics />} />
              <Route path="/voice-assistant" element={<VoiceAssistant />} />
              {/* v35.19 — /pr-cost-impact consolidated into /bill-detective (Engine #29 Code→Cost tab). */}
              <Route path="/pr-cost-impact" element={<Navigate to="/bill-detective" replace />} />
              <Route path="/health-score" element={<CostHealthScore />} />
              <Route path="/industry" element={<IndustryIntelligence />} />
              <Route path="/collaboration" element={<CostCollaboration />} />
              <Route path="/security-center" element={<SecurityCenter />} />
              <Route path="/bill-shock" element={<BillShockPrevention />} />
              <Route path="/export" element={<ExportCenter />} />
              <Route path="/connect" element={<IntegrationsMarketplace />} />
              <Route path="/integrations-marketplace" element={<Navigate to="/connect" replace />} />
              <Route path="/organization" element={<OrganizationPage />} />
              <Route path="/dashboard-builder" element={<DashboardBuilder />} />
              <Route path="/search" element={<AdvancedSearch />} />
              <Route path="/cost-graph" element={<CostIntelligenceGraph />} />
              <Route path="/notifications" element={<NotificationCenter />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              {/* Snowflake */}
              <Route path="/snowflake" element={<SnowflakeDashboard />} />
              <Route path="/snowflake-queries" element={<SnowflakeQueryExplorer />} />
              <Route path="/snowflake-alerts" element={<SnowflakeAlerts />} />
              {/* Databricks */}
              <Route path="/databricks" element={<DatabricksDashboard />} />
              {/* DigitalOcean */}
              <Route path="/digitalocean" element={<DigitalOceanDashboard />} />
              <Route path="/spike-autopsy" element={<SpikeAutopsy />} />
              <Route path="/query-estimator" element={<QueryEstimator />} />
              <Route path="/budget-countdown" element={<BudgetCountdown />} />
              <Route path="/team-chargeback" element={<TeamChargeback />} />
              <Route path="/storage-detective" element={<StorageDetective />} />
              {/* Killer Features */}
              <Route path="/spend-freeze" element={<SpendFreeze />} />
              {/* v35.18.2 — OneClickFix merged into /recommendations; redirect keeps old links alive */}
              <Route path="/one-click-fix" element={<Navigate to="/recommendations" replace />} />
              <Route path="/cost-conscience" element={<CostConscience />} />
              <Route path="/ai-cfo" element={<AICFO />} />
              <Route path="/peer-benchmark" element={<PeerBenchmark />} />
              {/* AI-Native Command Center */}
              <Route path="/ai-command-center" element={<AICommandCenter />} />
              {/* Admin Analytics */}
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              {/* Cost Forecasting */}
              <Route path="/cost-forecasting" element={<CostForecasting />} />
              {/* Anomaly Detection */}
              <Route path="/anomaly-detection" element={<Navigate to="/anomalies" replace />} />
              <Route path="/leaderboard" element={<Navigate to="/bill-detective" replace />} />
              <Route path="/saas-spend" element={<Navigate to="/saas-detective" replace />} />
              {/* Iter27 — short-name aliases the testing agent + customers commonly type */}
              <Route path="/forecasting" element={<Navigate to="/cost-forecasting" replace />} />
              <Route path="/budgets" element={<Navigate to="/budget-countdown" replace />} />
              <Route path="/commitments" element={<Navigate to="/commitment-coach" replace />} />
              {/* Admin Tools - P2 Features */}
              <Route path="/weekly-digest" element={<WeeklyDigest />} />
              <Route path="/ab-testing" element={<ABTesting />} />
              <Route path="/admin-settings" element={<AdminSettings />} />
              <Route path="/admin/server-modules" element={<AdminServerModules />} />
              <Route path="/admin/pre-flight" element={<AdminPreFlightPage />} />
              <Route path="/admin/audit-log" element={<AdminAuditLog />} />
              <Route path="/admin/soc2/evidence" element={<SOC2Evidence />} />
              <Route path="/admin/soc2-evidence" element={<SOC2Evidence />} />
              <Route path="/admin/pilot-activation" element={<PilotActivation />} />
              <Route path="/admin/p0-guide" element={<P0Guide />} />
              <Route path="/admin/cost-actuals" element={<CostActuals />} />
              <Route path="/admin/slack-setup" element={<SlackSetup />} />
              <Route path="/admin/signups" element={<AdminSignups />} />
              <Route path="/admin/cloud-billing-sync" element={<CloudBillingSync />} />
              <Route path="/admin/incident-log" element={<IncidentLog />} />
              {/* v35.18 — /admin/zombie-hunter consolidated into /idle-killer (Engine #28). Redirect to keep old links alive. */}
              <Route path="/admin/zombie-hunter" element={<Navigate to="/idle-killer" replace />} />
              <Route path="/admin/payback-filter" element={<PaybackFilter />} />
              <Route path="/admin/compliance-controls" element={<ComplianceControls />} />
              <Route path="/admin/anomaly-autopsy" element={<AnomalyAutopsy />} />
              <Route path="/admin/pr-cost-guardrail" element={<PRCostGuardrail />} />
              <Route path="/admin/roadmap-forecast" element={<RoadmapForecast />} />
              <Route path="/admin/saas-spend" element={<SaaSSpendIntelligence />} />
              <Route path="/admin/agent-economics" element={<AgentEconomics />} />
              <Route path="/admin/embedding-audit" element={<EmbeddingAudit />} />
              <Route path="/admin/idle-endpoints" element={<IdleEndpoints />} />
              <Route path="/admin/conversion-roi" element={<ConversionROI />} />
              <Route path="/admin/pipeline-dedup" element={<PipelineDedup />} />
              {/* v35.29 — Public aliases for our 3 hidden-gem engines: AI Agent Economics,
                  Embedding Store Audit, Pipeline DAG Dedup. The components themselves are
                  still auth-gated via the API layer, but the routes are public-facing so
                  the marquee + sitemap + footer can link to them. */}
              <Route path="/agent-economics" element={<AgentEconomics />} />
              <Route path="/embedding-audit" element={<EmbeddingAudit />} />
              <Route path="/pipeline-dedup" element={<PipelineDedup />} />
              <Route path="/admin/align-kickoff" element={<AlignKickoff />} />
              <Route path="/admin/hot-pilots" element={<HotPilots />} />
              <Route path="/admin/hn-launch" element={<HNLaunchAdmin />} />
              <Route path="/admin/cost-centers" element={<CostCenters />} />
              <Route path="/admin/feature-coverage" element={<AdminFeatureCoverage />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/conversion-funnel" element={<AdminConversionFunnel />} />
              <Route path="/settings/slack" element={<OrgSlackSettings /> } />
              <Route path="/admin/seo" element={<AdminSEO />} />
              <Route path="/admin/router-map" element={<AdminRouterMap />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsent />
          <FeedbackWidget />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <InstallAppPrompt />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
